import React, { useState, useEffect } from 'react';
import { Switch, Route, useRouteMatch, useHistory, Redirect, useLocation } from 'react-router-dom';
import { AgFinancing, Phase2 as AgFinancingPhase2, AgFinancingPhase2Status, AgStateName } from '@indigo-ag/schema';
import i18next from 'core/i18n';

import { CollateralUtil, Stepper, IStep, StepStatus } from 'shared';
import { FinancingProcessUtil } from 'shared/utils';
import { CollateralAnalysis } from '../BrazilGrowerFinance/features/collateral-analysis/CollateralAnalysis/CollateralAnalysis/CollateraAnalysis';
import { CollateralFormalization } from '../BrazilGrowerFinance/features/collateral-analysis/CollateralAnalysis/CollateralFormalization/CollateralFormalization';

export const PhaseRoutes = {
  [AgStateName.PHASE_1]: 'phase01',
  [AgStateName.PHASE_2]: 'phase02',
  [AgStateName.PHASE_3]: 'phase03',
  [AgStateName.PHASE_4]: 'phase03',
  [AgStateName.ON_HOLD]: 'phase02',
  [AgStateName.CLOSED]: 'phase01'
};

// Get correct path for a given step, if step does not have path look for parent path
const getStepPath = (allSteps: IStep[], step: IStep): string | null => {
  if (step.path) {
    return step.path;
  } else {
    const parent = allSteps.find(s => {
      return !!s.subSteps?.find(sub => sub.id === step.id);
    });

    if (parent && parent.path) {
      return parent.path;
    } else {
      return null;
    }
  }
};

const getStatus2 = (phase?: AgFinancingPhase2): StepStatus => {
  if (!phase) {
    return StepStatus.UNCOMPLETED;
  }

  if (phase.status === AgFinancingPhase2Status.APPROVED) {
    return StepStatus.COMPLETED;
  } else if (phase.status === AgFinancingPhase2Status.DENIED) {
    return StepStatus.DENIED;
  } else {
    return StepStatus.UNCOMPLETED;
  }
};

interface ExternalLegalTimelineProps {
  financingAgFinancing: AgFinancing;
}

const getStepsForLuchesi = (agFinancing: AgFinancing): IStep[] => {
  return [
    {
      id: '1',
      title: i18next.t('creditAnalysis'),
      completed: agFinancing.phase2 && agFinancing.phase2.status === AgFinancingPhase2Status.APPROVED,
      path: PhaseRoutes[AgStateName.PHASE_2],
      status: getStatus2(agFinancing.phase2),
      component: (
        <CollateralAnalysis
          collateral={agFinancing.phase2?.collateral}
          matriculas={agFinancing.phase2?.matriculas}
          phase2Files={agFinancing.phase2?.files}
          fieldMonitoring={agFinancing.phase2?.fieldMonitoring}
        />
      )
    },
    {
      id: '2',
      title: i18next.t('collateralFormalization'),
      completed: CollateralUtil.isFormalizationComplete(agFinancing),
      path: PhaseRoutes[AgStateName.PHASE_3],
      disabled: !agFinancing.phase2 || agFinancing.phase2.status !== AgFinancingPhase2Status.APPROVED,
      status: StepStatus.UNCOMPLETED,
      component: (
        <CollateralFormalization
          phase3Info={agFinancing.phase3}
          phase3Files={agFinancing.phase3?.files}
          matriculas={agFinancing.phase2?.matriculas}
        />
      )
    }
  ];
};

export const ExternalLegalTimeline: React.FunctionComponent<ExternalLegalTimelineProps> = ({
  financingAgFinancing
}) => {
  /**
   * Location
   */
  const location = useLocation();

  /**
   * State
   */
  const [activeStepId, setActiveStepId] = useState<IStep['id']>('1');
  const [steps, setSteps] = useState<IStep[]>([]);

  /**
   * Routes Matching
   */
  const { path, url } = useRouteMatch();

  /**
   * History
   */
  const history = useHistory();

  const getCurrentPhaseUrl = (): string => {
    const cPhase = FinancingProcessUtil.getCurrentPhase(financingAgFinancing);

    if (PhaseRoutes[cPhase] === PhaseRoutes.CLOSED) {
      return PhaseRoutes.PHASE_2;
    }

    return PhaseRoutes[cPhase];
  };

  // Returns path id that matches with location or first path by default
  const setActiveStepByLocation = () => {
    const phaseOnLocation = location.pathname
      .split('/')
      .slice(-1)
      .pop();

    const asId =
      steps.find(s => {
        if (s.path === phaseOnLocation) {
          return true;
        } else if (s.subSteps) {
          return !!s.subSteps?.find(sub => sub.path && sub.path === phaseOnLocation);
        } else {
          return false;
        }
      })?.id || steps[0].id!;

    setActiveStepId(asId);
  };

  const changeActiveStep = (step: IStep) => {
    setActiveStepId(step.id);

    // Navigate to route if exists
    const selectedStepRoutePath = getStepPath(steps, step);
    if (selectedStepRoutePath) {
      history.push(`${url}/${selectedStepRoutePath}`);
    }
  };

  useEffect(() => {
    if (steps.length) {
      setActiveStepByLocation();
    }
  }, [location, steps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (financingAgFinancing) {
      setSteps(getStepsForLuchesi(financingAgFinancing));
    }
  }, [financingAgFinancing]);

  return (
    <div className="flex">
      <Stepper steps={steps} activeStepId={activeStepId} onStepClick={changeActiveStep} />
      <div style={{ marginLeft: 50 }}>
        <Switch>
          {steps
            .filter(s => s.path && s.component && !s.disabled)
            .map(stepsWithRoute => (
              <Route key={`route-step-${stepsWithRoute.id}`} path={`${path}/${stepsWithRoute.path}`}>
                {stepsWithRoute.component}
              </Route>
            ))}
          <Redirect from={path} to={`${path}/${getCurrentPhaseUrl()}`} />
        </Switch>
      </div>
    </div>
  );
};
